import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Horizontal scroll container with left/right arrow visibility (ImprovementMode pattern).
 *
 * @param {Array<unknown>} deps - Re-run overflow checks when these change (e.g. tab count).
 * @param {number} [scrollAmount=300] - Pixels per arrow click.
 */
export function useHorizontalScrollArrows(deps = [], scrollAmount = 300) {
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }, []);

    const checkOverflow = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const { scrollWidth, clientWidth } = el;
        setShowRightArrow(scrollWidth > clientWidth);
    }, []);

    const scroll = useCallback(
        (direction) => {
            const el = scrollContainerRef.current;
            if (!el) return;
            const delta = direction === 'left' ? -scrollAmount : scrollAmount;
            el.scrollTo({ left: el.scrollLeft + delta, behavior: 'smooth' });
        },
        [scrollAmount]
    );

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return undefined;

        el.addEventListener('scroll', handleScroll);
        checkOverflow();
        handleScroll();

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
            handleScroll();
        });
        resizeObserver.observe(el);

        return () => {
            el.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies meaningful deps
    }, [handleScroll, checkOverflow, ...deps]);

    return { scrollContainerRef, showLeftArrow, showRightArrow, scroll };
}
